import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useAuth, useUser } from '@clerk/clerk-react';
import { syncEmail, registerTokenGetter } from './lib/api';

import AppLayout from './components/AppLayout';
import WizardLayout from './components/WizardLayout';
import Step1Photos from './pages/Step1Photos';
import Step2Identify from './pages/Step2Identify';
import Step3Aspects from './pages/Step3Aspects';
import Step4Details from './pages/Step3Details';
import Step5PriceShipping from './pages/Step5PriceShipping';
import Step6TitleDescription from './pages/Step6TitleDescription';
import Step7Photos from './pages/Step9Photos';
import Step8Preview from './pages/Step8Preview';
import SettingsPage from './pages/SettingsPage';
import DashboardPage from './pages/DashboardPage';
import ListingsPage from './pages/ListingsPage';
import OffersPage from './pages/OffersPage';
import CatalogPage from './pages/CatalogPage';
import SegmentsPage from './pages/SegmentsPage';

function AuthSync() {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  // Register token getter so axios interceptor can attach Bearer tokens
  useEffect(() => {
    registerTokenGetter(() => getToken());
  }, [getToken]);

  useEffect(() => {
    if (isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      syncEmail(user.primaryEmailAddress.emailAddress).catch(() => {});
    }
  }, [isSignedIn, user]);

  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <AuthSync />
      <Routes>
        <Route path="/sign-in/*" element={
          <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
            <div className="mb-8">
              <img src="/logo.png" alt="ListSamurAI" className="h-20 w-auto" />
            </div>
            <SignIn routing="path" path="/sign-in" />
          </div>
        } />
        <Route path="/sign-up/*" element={
          <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
            <div className="mb-8">
              <img src="/logo.png" alt="ListSamurAI" className="h-20 w-auto" />
            </div>
            <SignUp routing="path" path="/sign-up" />
          </div>
        } />

        <Route path="/" element={<RequireAuth><Navigate to="/dashboard" replace /></RequireAuth>} />

        {/* App shell — sidebar layout */}
        <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/listings/:tab" element={<ListingsPage />} />
          <Route path="/listings" element={<Navigate to="/listings/active" replace />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/segments" element={<SegmentsPage />} />
        </Route>

        <Route path="/listing/:id" element={<RequireAuth><WizardLayout /></RequireAuth>}>
          <Route path="step/1" element={<Step1Photos />} />
          <Route path="step/2" element={<Step2Identify />} />
          <Route path="step/3" element={<Step3Aspects />} />
          <Route path="step/4" element={<Step4Details />} />
          <Route path="step/5" element={<Step5PriceShipping />} />
          <Route path="step/6" element={<Step6TitleDescription />} />
          <Route path="step/7" element={<Step7Photos />} />
          <Route path="step/8" element={<Step8Preview />} />
          <Route index element={<Navigate to="step/1" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}
